import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Entries } from "type-fest";
import type { ExtendedValidationFieldConfig, ValidationRuleName } from "./types";

// Years must be 4-digit format and cannot start with zero
const YEAR_REGEX = /^[1-9]\d{3}$/;

interface ValidationProperties {
    // For min/max length
    value: number;
    validatorInstance: UiValidatorInstance;
}

// Lazy evaluate for SSR support
const getHandlers = (): Required<
    Record<ValidationRuleName, (value: string, properties: ValidationProperties) => boolean>
> => ({
    required: (value) => value.trim().length > 0,
    email: window.ui.validator.email,
    number: window.ui.validator.number,
    password: (value, properties) => window.ui.validator.password(value, properties.validatorInstance),
    tel: window.ui.validator.tel,
    url: window.ui.validator.url,
    year: (value) => !value || YEAR_REGEX.test(value),
    minLength: (value, properties) => window.ui.validator.minLength(value, properties.value),
    maxLength: (value, properties) => window.ui.validator.maxLength(value, properties.value),
});

export const useGenerateConfigWithPattern = () => {
    const { t } = useTranslation();
    return useCallback(
        (config: ExtendedValidationFieldConfig, validatorInstance: UiValidatorInstance): ValidationFieldConfig => {
            const { rules, ...rest } = config;
            const { custom, ...standardRules } = rules;

            const pattern: ValidationPatternCallback = ({ value, callback }) => {
                const handlers = getHandlers();

                for (const [rule, properties] of Object.entries(standardRules) as Entries<typeof standardRules>) {
                    if (properties) {
                        const validationProperties: ValidationProperties = {
                            value: typeof properties === "object" ? properties.value : -1,
                            validatorInstance,
                        };

                        const isValid = handlers[rule](value, validationProperties);
                        if (!isValid) {
                            let message = "";
                            if (typeof properties === "string") message = properties;
                            // MinLength | MaxLength
                            if (typeof properties === "object") {
                                message = properties.message ?? t(`validation.${rule}`, { value: properties.value });
                            }
                            if (!message) message = t(`validation.${rule}`);
                            callback(false, message);
                            return false;
                        }
                    }
                }

                // Custom validation rule
                if (custom) {
                    const isValid = custom.test(value);
                    if (!isValid) {
                        callback(false, custom.message);
                        return false;
                    }
                }

                callback(true);
                return true;
            };
            return { ...rest, pattern };
        },
        [t],
    );
};
